import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import useAdmin from '../hooks/useAdmin';
import axiosInstance from '../services/axiosInstance';

function ProtectedRoute({ children, requireAdmin = false, checkEstablishmentAccess = false }) {
  const { isAdmin, isLoading } = useAdmin();
  const { id_etablissement, establishmentId, userId } = useParams();
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const token = localStorage.getItem('token');

  // Récupérer l'ID de l'établissement depuis les paramètres d'URL
  const establishmentIdFromParams = id_etablissement || establishmentId;

  useEffect(() => {
    const checkEstablishmentPermission = async () => {
      console.log('=== DÉBUT VÉRIFICATION ACCÈS ===');
      console.log('checkEstablishmentAccess:', checkEstablishmentAccess);
      console.log('establishmentIdFromParams:', establishmentIdFromParams);
      console.log('isAdmin:', isAdmin);

      // Si checkEstablishmentAccess est false, autoriser l'accès
      if (checkEstablishmentAccess !== true) {
        console.log('Pas de vérification d\'établissement requise');
        setHasAccess(true);
        return;
      }

      // Si pas d'ID d'établissement dans l'URL, autoriser l'accès
      if (!establishmentIdFromParams) {
        console.log('Pas d\'ID établissement dans l\'URL');
        setHasAccess(true);
        return;
      }

      // Si l'utilisateur est admin, il a accès à tous les établissements
      if (isAdmin) {
        console.log('Utilisateur admin - accès autorisé à tous les établissements');
        setHasAccess(true);
        return;
      }

      setCheckingAccess(true);
      
      try {
        const partnerUserId = localStorage.getItem('user_id');
        console.log('Vérification d\'accès pour l\'établissement:', establishmentIdFromParams);
        console.log('Pour l\'utilisateur:', partnerUserId);

        if (!partnerUserId) {
          console.log('Pas d\'ID utilisateur trouvé');
          setHasAccess(false);
          return;
        }
        
        // Utiliser votre endpoint existant pour récupérer les établissements de l'utilisateur
        const response = await axiosInstance.get(`/partner_users/${partnerUserId}/get_establishments`);
        const userEstablishments = response.data.establishment || [];
        
        console.log('Établissements de l\'utilisateur:', userEstablishments);
        
        // Vérifier si l'utilisateur a accès à cet établissement
        const hasAccessToEstablishment = userEstablishments.some(
          est => est.id.toString() === establishmentIdFromParams.toString()
        );
        
        console.log('Accès trouvé:', hasAccessToEstablishment);
        console.log('Comparaison:', userEstablishments.map(est => `${est.id} === ${establishmentIdFromParams} ? ${est.id.toString() === establishmentIdFromParams.toString()}`));
        
        setHasAccess(hasAccessToEstablishment);
      } catch (error) {
        console.error('Erreur lors de la vérification d\'accès à l\'établissement:', error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
      
      console.log('=== FIN VÉRIFICATION ACCÈS ===');
    };

    if (!isLoading) {
      checkEstablishmentPermission();
    }
  }, [isAdmin, isLoading, checkEstablishmentAccess, establishmentIdFromParams]);

  // Afficher un loader pendant la vérification
  if (isLoading || (checkEstablishmentAccess === true && checkingAccess)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #059669',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#64748b',
            fontSize: '16px',
            margin: 0
          }}>
            {isLoading ? 'Vérification des permissions...' : 'Vérification de l\'accès à l\'établissement...'}
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Vérifier si l'utilisateur est connecté
  if (!token) {
    return <Navigate to="/" replace />;
  }

  // Vérifier si l'accès admin est requis
  if (requireAdmin && !isAdmin) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '48px',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          maxWidth: '500px'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '24px'
          }}>
            🚫
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '16px'
          }}>
            Accès non autorisé
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#64748b',
            marginBottom: '32px',
            lineHeight: '1.6'
          }}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette page. 
            Seuls les administrateurs peuvent accéder à cette section.
          </p>
          <button
            onClick={() => window.location.href = '/accueil'}
            style={{
              backgroundColor: '#059669',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Vérifier l'accès à l'établissement spécifique
  if (checkEstablishmentAccess === true && hasAccess === false) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '48px',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          maxWidth: '600px'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '24px'
          }}>
            🏪
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '16px'
          }}>
            Accès à l'établissement refusé
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#64748b',
            marginBottom: '16px',
            lineHeight: '1.6'
          }}>
            Vous n'avez pas l'autorisation d'accéder à l'établissement #{establishmentIdFromParams}. 
            Vous ne pouvez gérer que les établissements qui vous sont assignés.
          </p>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '32px',
            fontStyle: 'italic'
          }}>
            Vérifiez la console pour plus de détails sur les établissements disponibles.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.href = '/chr'}
              style={{
                backgroundColor: '#059669',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease-in-out'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
            >
              Mes établissements
            </button>
            <button
              onClick={() => window.location.href = '/accueil'}
              style={{
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease-in-out'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f1f5f9'}
            >
              Accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;